import { useEmbedInit } from "@src/app/hooks/useEmbedInit";
import type { BFFArticleBodyElement } from "@src/app/types/bff";
import { isThisUrlFrom as _isThisUrlFrom } from "@src/app/utils/tools";
import { Suspense, useEffect, useState } from "react";

import ArticleEmbedDatawrapper from "./components/ArticleEmbedDataWrapper";
import ArticleEmbedFacebook from "./components/ArticleEmbedFacebook";
import ArticleEmbedFlourish from "./components/ArticleEmbedFlourish";
import ArticleEmbedInstagram from "./components/ArticleEmbedInstagram";
import ArticleEmbedTikTok from "./components/ArticleEmbedTiktok";

const EmbedLoadingFallback = () => <div>Loading embed...</div>;

export default function ArticleEmbed({
  element,
}: {
  element: BFFArticleBodyElement["embed"];
}) {
  const { url = "", html = "" } = element;
  const [embedContent, setEmbedContent] = useState<JSX.Element | null>(null);

  useEmbedInit();

  useEffect(() => {
    if (!url && !html) return;

    const isThisUrlFrom = (from: string) => _isThisUrlFrom(url, from);

    const loadEmbedContent = () => {
      let content: typeof embedContent = null;

      switch (true) {
        case isThisUrlFrom("datawrapper"):
          content = <ArticleEmbedDatawrapper url={url} />;
          break;
        case isThisUrlFrom("facebook.com"):
          content = (
            <Suspense fallback={<EmbedLoadingFallback />}>
              <ArticleEmbedFacebook url={url} />
            </Suspense>
          );
          break;
        case isThisUrlFrom("tiktok.com"):
          content = (
            <Suspense fallback={<EmbedLoadingFallback />}>
              <ArticleEmbedTikTok url={url} />
            </Suspense>
          );
          break;
        case isThisUrlFrom("instagram.com"):
          content = (
            <Suspense fallback={<EmbedLoadingFallback />}>
              <ArticleEmbedInstagram url={url} />
            </Suspense>
          );
          break;
        case isThisUrlFrom("flourish.studio"):
          content = <ArticleEmbedFlourish url={url} />;
          break;
        case ["dugout.com", "vimeo.com", "bilibili.com"].some(isThisUrlFrom):
          content = (
            <iframe
              title={url}
              className="aspect-[16/9] w-full"
              src={url}
              allow="autoplay *; encrypted-media *;"
              allowFullScreen
              loading="lazy"
            />
          );
          break;
        case isThisUrlFrom("google.com") && url.includes("/forms/"):
          content = (
            <iframe
              title={url}
              className="w-full"
              height="500"
              src={url}
              loading="lazy"
            />
          );
          break;
        case isThisUrlFrom("polltab.com"):
          content = (
            <iframe
              title={url}
              className="w-full"
              height="500"
              src={url}
              loading="lazy"
            />
          );
          break;
        default:
          content = (
            <iframe
              className="w-full"
              title={url}
              src={url}
              allow="fullscreen; accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              loading="lazy"
            />
          );
      }

      setEmbedContent(content);
    };

    loadEmbedContent();
  }, [url, html]);

  if (!url && !html) return null;

  if (html) {
    return <div className="pb-4" dangerouslySetInnerHTML={{ __html: html }} />;
  }

  return (
    <div className="flex w-full justify-center pb-4">
      {embedContent || <EmbedLoadingFallback />}
    </div>
  );
}
